/**
 * SuccessWare BookIt Field Mappings by Company
 * 
 * Maps our service note fields to SuccessWare BookIt API input fields.
 * Each company may have different field requirements and mappings.
 */

export interface ServiceNoteFields {
  appointmentDate?: string;
  arrivalTimeWindow?: string;
  bookedBy?: string;
  serviceRequired?: string;
  customerIssues?: string;
  amountQuoted?: string;
  equipmentAge?: string;
  numberOfThermostats?: string;
  airHandlerLocation?: string;
  condenserLocation?: string;
  ladderNeeded?: string;
  ladderSize?: string;
  accessInstructions?: string;
  potentialHazards?: string;
}

export interface BookItJobInput {
  category: string;
  jobClass: string;
  jobType: string;
  jobTypeDescription?: string;
  serviceAccountId: string;
  locationId: string;
  departmentId: number;
  estimatedDuration: number;
  workRequestNotes?: string;
  startTimePreference?: string;
  endTimePreference?: string;
  leadSourceTypeId?: number;
  leadSourceId?: number;
}

export interface CompanyFieldMapping {
  companyId: string;
  companyName: string;
  tenantId: string;
  secondaryTenantId?: string;
  defaultDepartmentId: number;
  defaultJobCategory: string;
  defaultJobClass: string;
  defaultJobType: string;
  defaultEstimatedDuration: number;
  defaultLeadSourceTypeId?: number;
  
  // Field transformers: convert our fields to SuccessWare fields
  fieldMappers: {
    workRequestNotes: (notes: ServiceNoteFields) => string;
    jobTypeDescription: (notes: ServiceNoteFields) => string;
    startTimePreference: (notes: ServiceNoteFields) => string | undefined;
    endTimePreference: (notes: ServiceNoteFields) => string | undefined;
    scheduledFor: (notes: ServiceNoteFields) => string | undefined;
  };
}

/**
 * Company-specific mappings
 */
export const COMPANY_MAPPINGS: Record<string, CompanyFieldMapping> = {
  // Hales AC
  'hales-ac': {
    companyId: 'hales-ac',
    companyName: 'Hales AC',
    tenantId: '5013', // Primary tenant ID (shared)
    secondaryTenantId: '1001', // Company-specific secondary tenant
    defaultDepartmentId: 1, // HVAC Service Department
    defaultJobCategory: 'SERVICE',
    defaultJobClass: 'HVAC',
    defaultJobType: 'SERVICE_CALL',
    defaultEstimatedDuration: 120, // 2 hours in minutes
    defaultLeadSourceTypeId: 5, // Phone Call
    
    fieldMappers: {
      workRequestNotes: (notes: ServiceNoteFields) => {
        const parts: string[] = [];
        
        if (notes.serviceRequired) {
          parts.push(`SERVICE REQUIRED: ${notes.serviceRequired}`);
        }
        
        if (notes.customerIssues) {
          parts.push(`CUSTOMER ISSUES: ${notes.customerIssues}`);
        }
        
        if (notes.equipmentAge) {
          parts.push(`EQUIPMENT AGE: ${notes.equipmentAge}`);
        }
        
        if (notes.numberOfThermostats) {
          parts.push(`THERMOSTATS: ${notes.numberOfThermostats}`);
        }
        
        if (notes.airHandlerLocation) {
          parts.push(`AIR HANDLER LOCATION: ${notes.airHandlerLocation}`);
        }
        
        if (notes.condenserLocation) {
          parts.push(`CONDENSER LOCATION: ${notes.condenserLocation}`);
        }
        
        if (notes.ladderNeeded && notes.ladderNeeded !== 'unknown') {
          parts.push(`LADDER NEEDED: ${notes.ladderNeeded === 'yes' ? 'YES' : 'NO'}`);
          if (notes.ladderSize) {
            parts.push(`LADDER SIZE: ${notes.ladderSize}`);
          }
        }
        
        if (notes.accessInstructions) {
          parts.push(`ACCESS INSTRUCTIONS: ${notes.accessInstructions}`);
        }
        
        if (notes.potentialHazards) {
          parts.push(`POTENTIAL HAZARDS: ${notes.potentialHazards}`);
        }
        
        if (notes.amountQuoted) {
          parts.push(`AMOUNT QUOTED: ${notes.amountQuoted}`);
        }
        
        if (notes.bookedBy) {
          parts.push(`BOOKED BY: ${notes.bookedBy}`);
        }
        
        return parts.join('\n\n');
      },
      
      jobTypeDescription: (notes: ServiceNoteFields) => {
        return notes.serviceRequired || 'HVAC Service Call';
      },
      
      startTimePreference: (notes: ServiceNoteFields) => {
        if (notes.arrivalTimeWindow) {
          // Parse "8am-12pm" format
          const match = notes.arrivalTimeWindow.match(/(\d+)(am|pm)/i);
          if (match) {
            const hour = parseInt(match[1]);
            const period = match[2].toLowerCase();
            const militaryHour = period === 'pm' && hour !== 12 ? hour + 12 : hour;
            return `${militaryHour.toString().padStart(2, '0')}:00`;
          }
        }
        return undefined;
      },
      
      endTimePreference: (notes: ServiceNoteFields) => {
        if (notes.arrivalTimeWindow) {
          // Parse "8am-12pm" format
          const match = notes.arrivalTimeWindow.match(/(\d+)(am|pm)\s*-\s*(\d+)(am|pm)/i);
          if (match) {
            const hour = parseInt(match[3]);
            const period = match[4].toLowerCase();
            const militaryHour = period === 'pm' && hour !== 12 ? hour + 12 : hour;
            return `${militaryHour.toString().padStart(2, '0')}:00`;
          }
        }
        return undefined;
      },
      
      scheduledFor: (notes: ServiceNoteFields) => {
        return notes.appointmentDate || undefined;
      },
    },
  },
  
  // Climate Control Services
  'climate-control': {
    companyId: 'climate-control',
    companyName: 'Climate Control Services',
    tenantId: '5013', // Primary tenant ID (shared)
    secondaryTenantId: '1002', // Company-specific secondary tenant
    defaultDepartmentId: 1, // HVAC Service Department
    defaultJobCategory: 'SERVICE',
    defaultJobClass: 'HVAC',
    defaultJobType: 'SERVICE_CALL',
    defaultEstimatedDuration: 120, // 2 hours
    defaultLeadSourceTypeId: 5, // Phone Call
    
    fieldMappers: {
      workRequestNotes: (notes: ServiceNoteFields) => {
        const parts: string[] = [];
        
        if (notes.serviceRequired) {
          parts.push(`SERVICE: ${notes.serviceRequired}`);
        }
        
        if (notes.customerIssues) {
          parts.push(`PROBLEM: ${notes.customerIssues}`);
        }
        
        if (notes.equipmentAge) {
          parts.push(`EQUIP AGE: ${notes.equipmentAge}`);
        }
        
        if (notes.numberOfThermostats) {
          parts.push(`TSTATS: ${notes.numberOfThermostats}`);
        }
        
        if (notes.airHandlerLocation) {
          parts.push(`AHU: ${notes.airHandlerLocation}`);
        }
        
        if (notes.condenserLocation) {
          parts.push(`COND: ${notes.condenserLocation}`);
        }
        
        if (notes.ladderNeeded && notes.ladderNeeded !== 'unknown') {
          const ladderInfo = notes.ladderNeeded === 'yes' 
            ? `YES${notes.ladderSize ? ` (${notes.ladderSize})` : ''}`
            : 'NO';
          parts.push(`LADDER: ${ladderInfo}`);
        }
        
        if (notes.accessInstructions) {
          parts.push(`ACCESS: ${notes.accessInstructions}`);
        }
        
        if (notes.potentialHazards) {
          parts.push(`HAZARDS: ${notes.potentialHazards}`);
        }
        
        if (notes.amountQuoted) {
          parts.push(`QUOTED: ${notes.amountQuoted}`);
        }
        
        if (notes.bookedBy) {
          parts.push(`BY: ${notes.bookedBy}`);
        }
        
        return parts.join(' | ');
      },
      
      jobTypeDescription: (notes: ServiceNoteFields) => {
        return notes.serviceRequired || 'Service Call';
      },
      
      startTimePreference: (notes: ServiceNoteFields) => {
        if (notes.arrivalTimeWindow) {
          const match = notes.arrivalTimeWindow.match(/(\d+)(am|pm)/i);
          if (match) {
            const hour = parseInt(match[1]);
            const period = match[2].toLowerCase();
            const militaryHour = period === 'pm' && hour !== 12 ? hour + 12 : hour;
            return `${militaryHour.toString().padStart(2, '0')}:00`;
          }
        }
        return undefined;
      },
      
      endTimePreference: (notes: ServiceNoteFields) => {
        if (notes.arrivalTimeWindow) {
          const match = notes.arrivalTimeWindow.match(/(\d+)(am|pm)\s*-\s*(\d+)(am|pm)/i);
          if (match) {
            const hour = parseInt(match[3]);
            const period = match[4].toLowerCase();
            const militaryHour = period === 'pm' && hour !== 12 ? hour + 12 : hour;
            return `${militaryHour.toString().padStart(2, '0')}:00`;
          }
        }
        return undefined;
      },
      
      scheduledFor: (notes: ServiceNoteFields) => {
        return notes.appointmentDate || undefined;
      },
    },
  },
  
  // Abraham AC
  'abraham-ac': {
    companyId: 'abraham-ac',
    companyName: 'Abraham AC',
    tenantId: '5013', // Primary tenant ID (shared)
    secondaryTenantId: '1003', // Company-specific secondary tenant
    defaultDepartmentId: 1, // HVAC Service Department
    defaultJobCategory: 'SERVICE',
    defaultJobClass: 'HVAC',
    defaultJobType: 'SERVICE_CALL',
    defaultEstimatedDuration: 120, // 2 hours
    defaultLeadSourceTypeId: 5, // Phone Call
    
    fieldMappers: {
      workRequestNotes: (notes: ServiceNoteFields) => {
        const sections: string[] = [];
        
        // Primary service info
        if (notes.serviceRequired || notes.customerIssues) {
          const serviceInfo: string[] = [];
          if (notes.serviceRequired) serviceInfo.push(notes.serviceRequired);
          if (notes.customerIssues) serviceInfo.push(`Issue: ${notes.customerIssues}`);
          sections.push(serviceInfo.join(' - '));
        }
        
        // Equipment details
        const equipDetails: string[] = [];
        if (notes.equipmentAge) equipDetails.push(`Age: ${notes.equipmentAge}`);
        if (notes.numberOfThermostats) equipDetails.push(`Thermostats: ${notes.numberOfThermostats}`);
        if (equipDetails.length > 0) {
          sections.push(`EQUIPMENT: ${equipDetails.join(', ')}`);
        }
        
        // Location details
        const locationDetails: string[] = [];
        if (notes.airHandlerLocation) locationDetails.push(`AHU: ${notes.airHandlerLocation}`);
        if (notes.condenserLocation) locationDetails.push(`Condenser: ${notes.condenserLocation}`);
        if (locationDetails.length > 0) {
          sections.push(`LOCATION: ${locationDetails.join(', ')}`);
        }
        
        // Access requirements
        const accessDetails: string[] = [];
        if (notes.ladderNeeded && notes.ladderNeeded !== 'unknown') {
          accessDetails.push(`Ladder: ${notes.ladderNeeded === 'yes' ? notes.ladderSize || 'Yes' : 'No'}`);
        }
        if (notes.accessInstructions) accessDetails.push(notes.accessInstructions);
        if (accessDetails.length > 0) {
          sections.push(`ACCESS: ${accessDetails.join(' | ')}`);
        }
        
        // Safety & misc
        if (notes.potentialHazards) {
          sections.push(`HAZARDS: ${notes.potentialHazards}`);
        }
        
        if (notes.amountQuoted) {
          sections.push(`Quote: ${notes.amountQuoted}`);
        }
        
        if (notes.bookedBy) {
          sections.push(`Booked by: ${notes.bookedBy}`);
        }
        
        return sections.join('\n');
      },
      
      jobTypeDescription: (notes: ServiceNoteFields) => {
        return notes.serviceRequired || 'HVAC Service Request';
      },
      
      startTimePreference: (notes: ServiceNoteFields) => {
        if (notes.arrivalTimeWindow) {
          const match = notes.arrivalTimeWindow.match(/(\d+)(am|pm)/i);
          if (match) {
            const hour = parseInt(match[1]);
            const period = match[2].toLowerCase();
            const militaryHour = period === 'pm' && hour !== 12 ? hour + 12 : hour;
            return `${militaryHour.toString().padStart(2, '0')}:00`;
          }
        }
        return undefined;
      },
      
      endTimePreference: (notes: ServiceNoteFields) => {
        if (notes.arrivalTimeWindow) {
          const match = notes.arrivalTimeWindow.match(/(\d+)(am|pm)\s*-\s*(\d+)(am|pm)/i);
          if (match) {
            const hour = parseInt(match[3]);
            const period = match[4].toLowerCase();
            const militaryHour = period === 'pm' && hour !== 12 ? hour + 12 : hour;
            return `${militaryHour.toString().padStart(2, '0')}:00`;
          }
        }
        return undefined;
      },
      
      scheduledFor: (notes: ServiceNoteFields) => {
        return notes.appointmentDate || undefined;
      },
    },
  },
};

/**
 * Get field mapping configuration for a company
 */
export function getCompanyMapping(companyId: string): CompanyFieldMapping | undefined {
  return COMPANY_MAPPINGS[companyId];
}

/**
 * Transform service notes to BookIt job input
 */
export function transformServiceNotesToBookItInput(
  companyId: string,
  serviceNotes: ServiceNoteFields,
  serviceAccountId: string,
  locationId: string
): BookItJobInput | null {
  const mapping = getCompanyMapping(companyId);
  
  if (!mapping) {
    console.error(`[FieldMapping] No mapping found for company: ${companyId}`);
    return null;
  }
  
  const input: BookItJobInput = {
    category: mapping.defaultJobCategory,
    jobClass: mapping.defaultJobClass,
    jobType: mapping.defaultJobType,
    jobTypeDescription: mapping.fieldMappers.jobTypeDescription(serviceNotes),
    serviceAccountId,
    locationId,
    departmentId: mapping.defaultDepartmentId,
    estimatedDuration: mapping.defaultEstimatedDuration,
    workRequestNotes: mapping.fieldMappers.workRequestNotes(serviceNotes),
    leadSourceTypeId: mapping.defaultLeadSourceTypeId,
  };
  
  // Add time preferences if available
  const startTime = mapping.fieldMappers.startTimePreference(serviceNotes);
  if (startTime) {
    input.startTimePreference = startTime;
  }
  
  const endTime = mapping.fieldMappers.endTimePreference(serviceNotes);
  if (endTime) {
    input.endTimePreference = endTime;
  }
  
  return input;
}
